import axios from 'axios'
import { useContext, useRef, useState } from 'react'

// context
import { AppContext } from '../../App'

// components
import Button from '../button/button'
import InputFile from '../input-file/input-file'

// icons
import { ReactComponent as CloseIcon } from '../../assets/icons/close.svg'
import { ReactComponent as ImageIcon } from '../../assets/icons/image.svg'

// styles
import ProgressBar from '../progress-bar/progress-bar'
import styles from './file-uploader.module.scss'

interface PresignedUrlResponse {
	preSignedUrl: string
	fileUrl: string
	statusCode: number | undefined
	status: string | null
	imageId: string | null
	s3Uri: string | null
}

const UploadFileWithAbort: React.FC = () => {
	const appContext = useContext(AppContext)
	const apiURL = `${process.env.REACT_APP_API_URL}`

	const [file, setFile] = useState<File | null>(null) // Store selected file
	const [isUploading, setIsUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)

	const fileInputRef = useRef<HTMLInputElement | null>(null)

	const uploadFile = async () => {
		if (!file) return // Make sure a file is selected

		try {
			setIsUploading(true)
			setUploadProgress(0)

			// Step 1: Request a pre-signed URL from the backend
			const { data } = await axios.post<PresignedUrlResponse>(
				`${apiURL}upload`,
				{
					userId: '097',
					fileName: file.name,
					contentType: file.type,
					fileSize: file.size,
				}
			)

			// console.log('Presigned URL Response:', data)
			if (data.statusCode !== 200) {
				alert(`Error: ${data.statusCode}: ${data.status}`)
				return
			}

			const { s3Uri, preSignedUrl, fileUrl } = data

			// Step 2: Upload the file directly to S3 using the pre-signed URL
			await axios.put(preSignedUrl, file, {
				headers: { 'Content-Type': file.type },
				onUploadProgress: (progressEvent) => {
					if (progressEvent.total) {
						const progress = Math.round(
							(progressEvent.loaded / progressEvent.total) * 100
						)
						setUploadProgress(progress)
					}
				},
			})

			setUploadProgress(100)
			appContext?.setS3Uri(s3Uri || undefined)
			appContext?.setUploadedImage?.({
				fileName: file?.name,
				size: file?.size,
				url: fileUrl,
			})
			// console.log('Upload completed successfully')
		} catch (error) {
			console.error('Error during upload:', error)
			setUploadProgress(0)
		} finally {
			setIsUploading(false)
		}
	}

	const outputFileSize = (size: number): string => {
		const mb = 1000000
		const kb = 1000

		const divider = size >= mb ? mb : kb
		const label = size >= mb ? `MB` : `KB`

		return `${Math.ceil((size / divider) * 100) / 100}${label}`
	}

	return (
		<div className={styles.fileUploader}>
			{!isUploading && !appContext?.uploadedImage && (
				<div className={styles.fileStep}>
					<InputFile
						accept=".jpg,.png,.pdf"
						maxFileSize="32MB"
						onChange={(e) => {
							// Set file in state
							setFile(e.target.files ? e.target.files[0] : null)
						}}
						ref={fileInputRef}
					/>
					<Button
						type="button"
						theme="primary"
						label="Start Upload"
						disabled={!file ? true : undefined}
						onClick={uploadFile}
						className={styles.uploadButton}
					/>
				</div>
			)}

			{(isUploading || (!isUploading && appContext?.uploadedImage)) && (
				<div className={styles.uploadStep}>
					<div className={styles.imageContainer}>
						{appContext?.uploadedImage ? (
							<img
								src={appContext?.uploadedImage.url || ''}
								alt=""
							/>
						) : (
							<ImageIcon />
						)}
					</div>
					<div className={styles.progressContainer}>
						<p className={styles.fileName}>
							{appContext?.uploadedImage?.fileName || file?.name}
						</p>
						<p className={styles.fileSize}>
							{outputFileSize(
								appContext?.uploadedImage?.size ||
									file?.size ||
									0
							)}
						</p>
						{isUploading && (
							<ProgressBar percentage={uploadProgress} />
						)}
					</div>
					{!isUploading && (
						<button
							type="button"
							title="Remove"
							className={styles.cancelButton}
							onClick={() => {
								if (file) setFile(null)

								if (appContext?.uploadedImage) {
									appContext.setUploadedImage?.(undefined)
								}
							}}
						>
							<CloseIcon />
						</button>
					)}
				</div>
			)}
		</div>
	)
}

export default UploadFileWithAbort
